import { useEffect, useRef, useState } from "react";
import ChannelConnection from "../../js/connection/ChannelConnection";
import ConnectionData from "../../js/connection/ConnectionData";
import FocusButton from "./overlay/FocusButton";
import ControlButton from "./overlay/ControlButton";
import DrawingControls, { DrawingMode } from "./overlay/DrawingControls";
import RemoteControlPanel, {
    CameraControlAction,
    CameraControlOptions,
} from "./overlay/RemoteControlPanel";
import { showDangerToast } from "../../js/utils/toasts";

const HOLOCAM_ALIAS = "HoloCam";

type FocusMode = "annotate" | "control";

type TileRect = {
    id: string;
    left: number;
    top: number;
    width: number;
    height: number;
    alias: string;
    isLocal: boolean;
};

/**
 * Renders pin/annotate/control chrome in a React layer ABOVE #video.
 * LiveSwitch hardware <video> layers often paint over in-tile DOM overlays,
 * so chrome must live as a sibling of the video root — not inside .fm-video.
 */
export default function TileChromeLayer({ connection }: { connection: ChannelConnection }) {
    const layerRef = useRef<HTMLDivElement>(null);
    const [tiles, setTiles] = useState<TileRect[]>([]);
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const [focusMode, setFocusMode] = useState<FocusMode | null>(null);
    const [hasHoloCamInCall, setHasHoloCamInCall] = useState(false);
    const pendingModeRef = useRef<FocusMode | null>(null);
    const lastControlErrorAtRef = useRef(0);

    useEffect(() => {
        function syncTiles() {
            const layer = layerRef.current;
            if (!layer) return;

            const layerRect = layer.getBoundingClientRect();
            const connections = [
                ...Object.values(connection.sfuConnection.connections),
                ...connection.sfuConnection.getUnfocusedConnections(),
            ].filter((c): c is ConnectionData => !!c?.view);

            setHasHoloCamInCall(
                connections.some((c) => c.userAlias === HOLOCAM_ALIAS)
            );

            const next: TileRect[] = [];
            for (const c of connections) {
                const r = c.view.getBoundingClientRect();
                if (r.width < 8 || r.height < 8) continue;
                next.push({
                    id: c.id,
                    left: r.left - layerRect.left,
                    top: r.top - layerRect.top,
                    width: r.width,
                    height: r.height,
                    alias: c.userAlias || "Participant",
                    isLocal: !!c.isLocal,
                });
            }
            setTiles(next);
        }

        function onFocus(data: ConnectionData | null) {
            setFocusedId(data?.id ?? null);
            if (!data) {
                pendingModeRef.current = null;
                setFocusMode(null);
                connection.events.notify("onfocusmode", { id: null, mode: null });
                return;
            }

            const mode = pendingModeRef.current ?? "annotate";
            pendingModeRef.current = null;
            setFocusMode(mode);
            connection.events.notify("onfocusmode", { id: data.id, mode });
        }

        connection.events.addListener("onvideolayout", syncTiles);
        connection.events.addListener("onviewfocus", onFocus);
        connection.events.addListener("onconnectionopen", syncTiles);
        connection.events.addListener("onconnectionclose", syncTiles);

        syncTiles();
        const t1 = window.setTimeout(syncTiles, 100);
        const t2 = window.setTimeout(syncTiles, 500);
        const iv = window.setInterval(syncTiles, 1000);
        window.addEventListener("resize", syncTiles);

        return () => {
            connection.events.removeListener("onvideolayout", syncTiles);
            connection.events.removeListener("onviewfocus", onFocus);
            connection.events.removeListener("onconnectionopen", syncTiles);
            connection.events.removeListener("onconnectionclose", syncTiles);
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.clearInterval(iv);
            window.removeEventListener("resize", syncTiles);
        };
    }, [connection]);

    useEffect(() => {
        function onCameraControlResult(result: { ok: boolean; error?: string }) {
            if (result.ok) return;
            const now = Date.now();
            if (now - lastControlErrorAtRef.current < 4000) return;
            lastControlErrorAtRef.current = now;
            showDangerToast(result.error || "The HoloCam could not execute that command.");
        }

        connection.events.addListener("oncameracontrolresult", onCameraControlResult);
        return () => {
            connection.events.removeListener("oncameracontrolresult", onCameraControlResult);
        };
    }, [connection]);

    function resolveConnection(tileId: string): ConnectionData | undefined {
        return (
            connection.sfuConnection.connections[tileId] ??
            connection.sfuConnection
                .getUnfocusedConnections()
                .find((c) => c.id === tileId)
        );
    }

    function enterAnnotate(tileId: string) {
        pendingModeRef.current = "annotate";
        connection.focusView(tileId);
    }

    function enterControl(tileId: string) {
        if (focusedId === tileId && focusMode === "control") {
            exitFocus();
            return;
        }
        pendingModeRef.current = "control";
        connection.focusView(tileId);
        const target = resolveConnection(tileId);
        if (target) {
            connection.messager.sendControlRequest(target);
        }
    }

    function exitFocus() {
        if (focusedId) {
            connection.events.notify("ondrawingmode", { id: focusedId, mode: "none" });
        }
        pendingModeRef.current = null;
        connection.focusView(null);
    }

    function onModeChanged(mode: DrawingMode) {
        if (!focusedId) return;
        connection.events.notify("ondrawingmode", { id: focusedId, mode });
    }

    function onColorChanged(color: string) {
        if (!focusedId) return;
        connection.events.notify("ondrawingcolor", { id: focusedId, color });
    }

    function onCameraCommand(action: CameraControlAction, options?: CameraControlOptions) {
        if (!focusedId) return;
        const target = resolveConnection(focusedId);
        if (target) {
            connection.messager.sendCameraControl(target, action, options).fail((error) => {
                const now = Date.now();
                if (now - lastControlErrorAtRef.current < 4000) return;
                lastControlErrorAtRef.current = now;
                showDangerToast(error?.message || "The camera command could not be sent.");
            });
        }
    }

    const focusedTile = tiles.find((t) => t.id === focusedId);
    const showAnnotateTools = !!focusedTile && focusMode === "annotate";
    const showControlPanel = !!focusedTile && focusMode === "control";

    return (
        <div ref={layerRef} className="tile-chrome-layer" aria-hidden={tiles.length === 0}>
            {tiles.map((tile) => (
                <div
                    key={tile.id}
                    className="tile-chrome-item"
                    style={{
                        left: tile.left,
                        top: tile.top,
                        width: tile.width,
                        height: tile.height,
                    }}
                >
                    <div className="tile-chrome-pin">
                        {hasHoloCamInCall && tile.alias === HOLOCAM_ALIAS && (
                            <ControlButton
                                isActive={focusedId === tile.id && focusMode === "control"}
                                onClick={() => enterControl(tile.id)}
                            />
                        )}
                        <FocusButton
                            isFocused={focusedId === tile.id && focusMode === "annotate"}
                            setFocused={(focused) => {
                                if (focused) enterAnnotate(tile.id);
                                else exitFocus();
                            }}
                        />
                    </div>
                </div>
            ))}

            {showAnnotateTools && focusedTile && (
                <div
                    className="tile-chrome-draw"
                    style={{
                        left: focusedTile.left,
                        top: focusedTile.top,
                        width: focusedTile.width,
                        height: focusedTile.height,
                    }}
                >
                    <div className="tile-chrome-draw-bar">
                        <DrawingControls
                            onModeChanged={onModeChanged}
                            onColorChanged={onColorChanged}
                            onExit={exitFocus}
                        />
                    </div>
                </div>
            )}

            {showControlPanel && focusedTile && (
                <div
                    className="tile-chrome-control"
                    style={{
                        left: focusedTile.left,
                        top: focusedTile.top,
                        width: focusedTile.width,
                        height: focusedTile.height,
                    }}
                >
                    <div className="tile-chrome-control-panel">
                        <RemoteControlPanel
                            onCommand={onCameraCommand}
                            onExit={exitFocus}
                        />
                    </div>
                </div>
            )}

            <style>{`
                .tile-chrome-layer {
                    position: absolute;
                    inset: 0;
                    z-index: 28;
                    pointer-events: none;
                    overflow: hidden;
                }

                .tile-chrome-item {
                    position: absolute;
                    pointer-events: none;
                }

                .tile-chrome-pin {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    gap: 8px;
                    pointer-events: auto;
                    z-index: 2;
                }

                .tile-chrome-draw,
                .tile-chrome-control {
                    position: absolute;
                    pointer-events: none;
                    z-index: 3;
                }

                .tile-chrome-draw-bar {
                    position: absolute;
                    bottom: 56px;
                    left: 50%;
                    transform: translateX(-50%);
                    pointer-events: auto;
                }

                .tile-chrome-control-panel {
                    position: absolute;
                    bottom: 56px;
                    right: 16px;
                    width: min(280px, calc(100% - 32px));
                    pointer-events: auto;
                    box-sizing: border-box;
                }

                @media (max-width: 768px) {
                    .tile-chrome-pin {
                        top: 8px;
                        right: 8px;
                    }
                    .tile-chrome-draw-bar {
                        bottom: 44px;
                        max-width: calc(100% - 16px);
                    }
                    .tile-chrome-control-panel {
                        bottom: 44px;
                        right: 8px;
                        left: auto;
                        width: min(260px, calc(100% - 16px));
                    }
                    .tile-chrome-draw-bar .drawing-toolbar-inner {
                        max-width: 100%;
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                        scrollbar-width: none;
                    }
                    .tile-chrome-draw-bar .drawing-toolbar-inner::-webkit-scrollbar {
                        display: none;
                    }
                }

                @media (max-width: 480px) {
                    .tile-chrome-draw-bar {
                        bottom: 36px;
                        left: 8px;
                        right: 8px;
                        transform: none;
                        width: auto;
                    }
                    .tile-chrome-draw-bar .drawing-toolbar {
                        justify-content: stretch;
                    }
                    .tile-chrome-control-panel {
                        bottom: 36px;
                    }
                }
            `}</style>
        </div>
    );
}
